import { useState } from "react";
import { auth as authApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import { Mail, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Full-screen animated email verification overlay.
 * Shown once per session to unverified users.
 * Dismissible — full app access is preserved.
 */
export default function VerifyEmailBanner() {
  const { user, impersonatedBy } = useAuth();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("kiddo_verify_dismissed") === "1"
  );
  const [sending, setSending]   = useState(false);
  const [sent, setSent]         = useState(false);

  const HIDDEN_ROUTES = ["/disa", "/login", "/signup", "/free-screening", "/verify-email"];
  if (
    !user ||
    user.email_verified ||
    impersonatedBy ||
    dismissed ||
    HIDDEN_ROUTES.some((r) => pathname.startsWith(r))
  ) return null;

  const dismiss = () => {
    sessionStorage.setItem("kiddo_verify_dismissed", "1");
    setDismissed(true);
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await authApi.requestVerification();
      if (r?.already) {
        toast.success("Email already verified.");
        dismiss();
      } else {
        setSent(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send verification email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes veb-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes veb-slide-up {
          from { opacity: 0; transform: translateY(32px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes veb-pulse-ring {
          0%   { transform: scale(1);    opacity: 0.5; }
          100% { transform: scale(2.2);  opacity: 0;   }
        }
        @keyframes veb-float {
          0%,100% { transform: translateY(0px); }
          50%      { transform: translateY(-8px); }
        }
        @keyframes veb-dot-drift {
          0%   { transform: translate(0,0) scale(1);   opacity: 0.18; }
          50%  { transform: translate(12px,-18px) scale(1.2); opacity: 0.28; }
          100% { transform: translate(0,0) scale(1);   opacity: 0.18; }
        }
        .veb-overlay      { animation: veb-fade-in  .25s ease both; }
        .veb-card         { animation: veb-slide-up .35s cubic-bezier(.22,1,.36,1) .05s both; }
        .veb-ring         { animation: veb-pulse-ring 2s ease-out infinite; }
        .veb-ring2        { animation: veb-pulse-ring 2s ease-out .7s infinite; }
        .veb-icon         { animation: veb-float 3.2s ease-in-out infinite; }
        .veb-dot-a        { animation: veb-dot-drift 7s ease-in-out infinite; }
        .veb-dot-b        { animation: veb-dot-drift 9s ease-in-out 2s infinite; }
        .veb-dot-c        { animation: veb-dot-drift 6s ease-in-out 4s infinite; }
      `}</style>

      {/* Backdrop */}
      <div
        className="veb-overlay no-print fixed inset-0 z-[300] flex items-center justify-center px-4"
        style={{ background: "rgba(10,30,27,0.72)", backdropFilter: "blur(6px)" }}
        data-testid="verify-email-banner"
      >
        {/* Decorative drifting dots */}
        <span className="veb-dot-a pointer-events-none absolute w-64 h-64 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(45,212,191,.25) 0%, transparent 70%)", top: "12%", left: "8%" }} />
        <span className="veb-dot-b pointer-events-none absolute w-72 h-72 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(45,212,191,.18) 0%, transparent 70%)", bottom: "10%", right: "6%" }} />
        <span className="veb-dot-c pointer-events-none absolute w-40 h-40 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(255,255,255,.08) 0%, transparent 70%)", top: "55%", left: "60%" }} />

        {/* Card */}
        <div
          className="veb-card relative w-full max-w-sm md:max-w-md bg-white dark:bg-[#0e2520] rounded-3xl overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.35)]"
        >

          {/* Teal header band */}
          <div className="relative bg-gradient-to-br from-teal-500 to-teal-700 pt-10 pb-12 flex flex-col items-center overflow-hidden">
            {/* Background grid pattern */}
            <div className="absolute inset-0 opacity-[0.07]"
              style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "20px 20px" }} />

            {/* Pulse rings behind the icon */}
            <div className="relative flex items-center justify-center mb-1">
              <span className="veb-ring  absolute w-24 h-24 rounded-full border-2 border-white/40" />
              <span className="veb-ring2 absolute w-24 h-24 rounded-full border-2 border-white/25" />

              {/* Icon circle */}
              <div className="veb-icon relative z-10 w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-[0_4px_24px_rgba(0,0,0,0.2)]">
                {sent
                  ? <CheckCircle2 className="w-9 h-9 text-white" strokeWidth={2} />
                  : <Mail className="w-9 h-9 text-white" strokeWidth={1.8} />
                }
              </div>
            </div>
          </div>

          {/* Curved white lift */}
          <div className="-mt-6 bg-white dark:bg-[#0e2520] rounded-t-[2rem] pt-6 pb-8 px-6 md:px-8 flex flex-col items-center text-center">
            {sent ? (
              <>
                <h2 className="font-kp font-extrabold text-[22px] text-kp-ink leading-tight">
                  Check your inbox
                </h2>
                <p className="text-[14px] text-kp-ink2 mt-2 leading-snug max-w-[260px]">
                  We sent a verification link to{" "}
                  <span className="font-semibold text-kp-ink break-all">{user.email}</span>.
                  Click it to activate your account.
                </p>

                <div className="mt-5 w-full rounded-2xl bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700 px-4 py-3 text-[13px] text-teal-700 dark:text-teal-300 leading-snug">
                  Didn&apos;t get it? Check spam or{" "}
                  <button type="button" onClick={send} disabled={sending}
                    className="font-bold underline underline-offset-2 disabled:opacity-60">
                    resend
                  </button>
                  .
                </div>

                <button
                  type="button"
                  onClick={dismiss}
                  data-testid="send-verify-email-btn"
                  className="mt-5 w-full rounded-full bg-teal-600 hover:bg-teal-700 active:scale-[0.98] text-white font-kp font-bold text-[15px] py-3.5 transition-all flex items-center justify-center gap-2 shadow-[0_4px_16px_rgba(13,148,136,0.35)]"
                >
                  Continue to Kiddo+ <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </>
            ) : (
              <>
                <h2 className="font-kp font-extrabold text-[22px] text-kp-ink leading-tight">
                  Verify your email
                </h2>
                <p className="text-[14px] text-kp-ink2 mt-2 leading-snug max-w-[270px]">
                  Confirm{" "}
                  <span className="font-semibold text-kp-ink break-all">{user.email}</span>{" "}
                  to keep your Kiddo+ account secure.
                </p>

                <button
                  type="button"
                  onClick={send}
                  disabled={sending}
                  data-testid="send-verify-email-btn"
                  className="mt-6 w-full rounded-full bg-teal-600 hover:bg-teal-700 active:scale-[0.98] text-white font-kp font-bold text-[15px] py-3.5 transition-all flex items-center justify-center gap-2 shadow-[0_4px_16px_rgba(13,148,136,0.35)] disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send verification link <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={dismiss}
                  className="mt-3 text-[13px] text-kp-ink2 hover:text-kp-ink underline underline-offset-2 transition-colors"
                >
                  Remind me later
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
